/**
 * 설계·튜닝 가이드 **문서 보기**(2026-10-15) — 가이드를 열면 먼저 이 모양으로 읽힌다. 고치려면 「편집」 으로 편집 화면(GuideRulesEditor)에 들어간다.
 *
 * 편집 상태(form)를 그대로 그린다 — 저장 전 고친 내용도 같은 모양으로 보인다. 빈 규칙(원칙 없음)·빈 예외 줄은 그리지 않는다.
 */
import React from 'react';
import PropTypes from 'prop-types';

const filled = (s) => !!String(s || '').trim();

function GuideDocView({ form, fixTopics, showMappings, t }) {
    const sections = (form.sections || [])
        .map(s => ({ ...s, rules: (s.rules || []).filter(r => filled(r.principle)) }))
        .filter(s => s.rules.length);
    const labelOf = (code) => (fixTopics.find(x => x.code === code) || {}).label || code;
    const pri = form.priority || {};
    const values = String(pri.values || '').split('\n').map(x => x.trim()).filter(Boolean);
    const hasPri = form.kind === 'tuning' && ((pri.fixOrder || []).length || values.length || filled(pri.stopAt));
    let no = 0;
    return (
        <article className="gd-doc">
            <h2 className="gd-title">{form.title || t('assistant:designGuide.doc.untitled')}</h2>
            {filled(form.description) ? <p className="gd-desc">{form.description}</p> : null}

            {sections.length ? sections.map(s => (
                <section key={s.key} className="gd-section">
                    {filled(s.topic) || filled(s.subtopic) ? (
                        <h3 className="gd-topic">
                            {s.topic}
                            {filled(s.subtopic) ? <span className="gd-subtopic">{filled(s.topic) ? ' › ' : ''}{s.subtopic}</span> : null}
                        </h3>
                    ) : null}
                    <ol className="gd-rules">
                        {s.rules.map(r => {
                            no += 1;
                            const ex = (r.exceptions || []).filter(e => filled(e.when) || filled(e.action));
                            return (
                                <li key={r.key} className="gd-rule">
                                    <span className="gd-no">R{no}</span>
                                    <div className="gd-ruleBody">
                                        {filled(r.when) ? <div className="gd-when">{r.when}</div> : null}
                                        <div className="gd-principle">{r.principle}</div>
                                        {filled(r.explain) ? <div className="gd-explain">{r.explain}</div> : null}
                                        {ex.length ? (
                                            <ul className="gd-exceptions">
                                                {ex.map((e, i) => (
                                                    // eslint-disable-next-line react/no-array-index-key -- 예외 줄에는 고유한 키가 없다(순서가 곧 정체)
                                                    <li key={i}>
                                                        <span className="gd-exTag">{t('assistant:designGuide.doc.exception')}</span>
                                                        {filled(e.when) ? <span className="gd-exWhen">{e.when}</span> : null}
                                                        {filled(e.when) && filled(e.action) ? ' → ' : null}
                                                        {e.action}
                                                        {filled(e.explain) ? <div className="gd-explain">{e.explain}</div> : null}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : null}
                                        {filled(r.basis) ? <div className="gd-basis">{t('assistant:designGuide.doc.basis')}: {r.basis}</div> : null}
                                    </div>
                                </li>
                            );
                        })}
                    </ol>
                </section>
            )) : <p className="gd-empty">{t('assistant:designGuide.doc.noRules')}</p>}

            {hasPri ? (
                <section className="gd-section">
                    <h3 className="gd-topic">{t('assistant:tuningGuide.priority.title')}</h3>
                    {(pri.fixOrder || []).length ? (
                        <>
                            <div className="gd-subhead">{t('assistant:tuningGuide.priority.fixOrder')}</div>
                            <ol className="gd-plain">{pri.fixOrder.map(code => <li key={code}>{labelOf(code)}</li>)}</ol>
                        </>
                    ) : null}
                    {values.length ? (
                        <>
                            <div className="gd-subhead">{t('assistant:tuningGuide.priority.values')}</div>
                            <ol className="gd-plain">{values.map(v => <li key={v}>{v}</li>)}</ol>
                        </>
                    ) : null}
                    {filled(pri.stopAt) ? (
                        <div className="gd-subhead">{t('assistant:designGuide.doc.stopAt', { pct: pri.stopAt })}</div>
                    ) : null}
                </section>
            ) : null}

            {showMappings ? (
                <section className="gd-section">
                    <h3 className="gd-topic">{t('assistant:designGuide.f.mappings')}</h3>
                    {(form.mappings || []).filter(m => m.connectionId).length ? (
                        <ul className="gd-plain">
                            {form.mappings.filter(m => m.connectionId).map(m => (
                                <li key={`${m.connectionId}/${m.schema}`}>{m.connName || m.connectionId}{m.schema ? ` · ${m.schema}` : ''}</li>
                            ))}
                        </ul>
                    ) : <p className="gd-empty">{t('assistant:designGuide.doc.noMappings')}</p>}
                    {!form.active ? <p className="gd-empty">{t('assistant:designGuide.doc.inactive')}</p> : null}
                </section>
            ) : null}
        </article>
    );
}

GuideDocView.propTypes = {
    form: PropTypes.shape({}).isRequired,
    fixTopics: PropTypes.arrayOf(PropTypes.shape({ code: PropTypes.string, label: PropTypes.string })),
    showMappings: PropTypes.bool,
    t: PropTypes.func.isRequired,
};
GuideDocView.defaultProps = { fixTopics: [], showMappings: false };

export default GuideDocView;
